import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import exportingInit from 'highcharts/modules/exporting';
import exportDataInit from 'highcharts/modules/export-data';

// Initialize the exporting and export data modules
exportingInit(Highcharts);
exportDataInit(Highcharts);

const DailyAverageChart = ({ chartData }) => {
  const [chartOptions, setChartOptions] = useState(null);

  useEffect(() => {
    if (!chartData) return;

    // Helper function to aggregate data by day and calculate the average
    const aggregateDailyAverages = (data, key) => {
      const dailyData = {};

      data.forEach(item => {
        const date = new Date(item.timestamp).toISOString().split('T')[0]; // Get date only (YYYY-MM-DD)
        if (!dailyData[date]) {
          dailyData[date] = { sum: 0, count: 0 };
        }
        dailyData[date].sum += item[key];
        dailyData[date].count += 1;
      });

      return Object.keys(dailyData).map(date => [
        new Date(date).getTime(),
        dailyData[date].sum / dailyData[date].count // Calculate average
      ]);
    };

    const dailyAverageProduction = aggregateDailyAverages(chartData, 'production');
    const dailyAverageConsumption = aggregateDailyAverages(chartData, 'consumption');

    const productionColor = Highcharts.getOptions().colors[0];
    const consumptionColor = Highcharts.getOptions().colors[1];

    setChartOptions({
      chart: {
        zoomType: 'x'
      },
      title: {
        text: null
      },
      xAxis: {
        type: 'datetime',
        title: {
          text: 'Date'
        }
      },
      yAxis: {
        gridLineWidth: 0,
        title: {
          text: 'Energy (kWh)'
        }
      },
      legend: {
        enabled: true
      },
      plotOptions: {
        area: {
          fillColor: {
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1
            },
            stops: [
              [0, productionColor],
              [1, Highcharts.color(productionColor).setOpacity(0).get('rgba')]
            ]
          },
          marker: {
            radius: 2
          },
          lineWidth: 1,
          states: {
            hover: {
              lineWidth: 1
            }
          },
          threshold: null
        }
      },
      series: [
        {
          type: 'area',
          name: 'Daily Average Production',
          data: dailyAverageProduction,
          color: productionColor,
          showInLegend: true,
          fillColor: {
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1
            },
            stops: [
              [0, Highcharts.color(productionColor).setOpacity(0.5).get('rgba')],
              [1, Highcharts.color(productionColor).setOpacity(0).get('rgba')]
            ]
          },
        },
        {
          type: 'area',
          name: 'Daily Average Consumption',
          data: dailyAverageConsumption,
          color: consumptionColor,
          showInLegend: true,
          fillColor: {
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1
            },
            stops: [
              [0, Highcharts.color(consumptionColor).setOpacity(0.5).get('rgba')],
              [1, Highcharts.color(consumptionColor).setOpacity(0).get('rgba')]
            ]
          },
        }
      ],
      exporting: {
        enabled: true
      },
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: false
      }
    });
  }, [chartData]);

  if (!chartOptions || !chartData) return null;

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      containerProps={{ style: { height: '100%', width: '100%' } }}
    />
  );
};

export default DailyAverageChart;
